import { HttpStatusCode } from "axios";
import { auth } from "config/firebase.config";
import { EPath } from "constants/path";
import { SecureStorageKeys } from "constants/secureStorage";
import {
  AuthenticationApi,
  Configuration,
  ErrorResponse,
  ErrorResponseFromJSON,
  Middleware,
  ResponseError,
} from "libs/cellera-sdk";

import { secureStorage } from "./secureStorage";

// Flag to prevent infinite refresh loops
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Queue to hold pending requests while token is being refreshed
let pendingRequests: Array<{
  resolve: (token: string | null) => void;
  reject: (error: Error) => void;
}> = [];

// Reusable auth API instance for refresh (created once to avoid overhead)
let refreshAuthApi: AuthenticationApi | null = null;

export const extractErrorMessage = async (
  response: Response
): Promise<string> => {
  try {
    const responseText = await response.clone().text();
    const errorResponse: ErrorResponse = ErrorResponseFromJSON(
      JSON.parse(responseText)
    );

    // Return the most specific error message available
    if (errorResponse?.messages && errorResponse?.messages?.length > 0) {
      return errorResponse?.messages?.join(", ");
    }
    if (errorResponse?.message) {
      return errorResponse.message;
    }
    if (errorResponse?.errorCode) {
      return `Error ${errorResponse?.errorCode}`;
    }
  } catch (parseError) {
    console.warn("Could not parse error response:", parseError);
  }

  return "An unknown error occurred";
};

const checkPlatformAuthorization = async (): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return false;
    }
    const idTokenResult = await currentUser.getIdTokenResult();
    return idTokenResult.claims.role === "provider";
  } catch (error) {
    console.error("Error checking platform authorization:", error);
    return false;
  }
};

/**
 * Check if the user requires onboarding by examining their custom claims
 * Returns true if user is authenticated and needs onboarding
 */
const checkIfRequiresOnboarding = async (): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return false;
    }

    const idTokenResult = await currentUser.getIdTokenResult();
    return idTokenResult.claims.requiresOnboarding === true;
  } catch (error) {
    console.error("Error checking onboarding status:", error);
    return false;
  }
};

/**
 * Attempts to refresh the expired token using Firebase auth
 * Queues concurrent refresh requests to avoid multiple simultaneous refresh calls
 * Returns the new token if successful, null otherwise
 */
const refreshToken = async (): Promise<string | null> => {
  // If already refreshing, queue this request and wait for the ongoing refresh
  if (isRefreshing && refreshPromise) {
    return new Promise((resolve, reject) => {
      pendingRequests.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.warn("No authenticated user found for token refresh");
        return null;
      }

      const uid = currentUser.uid;

      // Lazy initialize auth API for refresh (reuse to avoid creating new instance each time)
      if (!refreshAuthApi) {
        const refreshConfig = new Configuration({
          basePath: process.env.REACT_APP_CELLERA_API_BASE_URL,
        });
        refreshAuthApi = new AuthenticationApi(refreshConfig);
      }

      // Call the refresh token API to update server-side session
      await refreshAuthApi.refreshToken({
        refreshTokenRequest: {
          uid: uid,
        },
      });

      // Get a fresh Firebase ID token
      const freshIdToken = await currentUser.getIdToken(true);

      // Update the stored token
      secureStorage.set(SecureStorageKeys.CUSTOM_TOKEN, freshIdToken);
      console.log("Token refreshed successfully");

      // Resolve all pending requests with the new token
      pendingRequests.forEach(({ resolve }) => resolve(freshIdToken));
      pendingRequests = [];

      return freshIdToken;
    } catch (error) {
      console.error("Token refresh failed:", error);

      // Reject all pending requests
      const refreshError = new Error("Token refresh failed");
      pendingRequests.forEach(({ reject }) => reject(refreshError));
      pendingRequests = [];

      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Utility function to handle token expiration and redirect to login
 * This function can be called from anywhere in the app when token expiration is detected
 */
export const handleTokenExpiration = () => {
  console.warn(
    "Token expired or unauthorized access detected. Redirecting to login..."
  );

  // Clear the stored token
  secureStorage.remove(SecureStorageKeys.CUSTOM_TOKEN);

  // Redirect to login page
  // Use window.location.href to ensure a full page reload and clear any cached state
  window.location.href = EPath.LOGIN;
};

const handlePermissionError = async () => {
  const isPlatformAuthorized = await checkPlatformAuthorization();
  if (!isPlatformAuthorized) {
    throw new Error(
      "You’re using the wrong account type to sign in. Please try again with the correct account."
    );
  }

  console.warn("Permission error detected. Checking onboarding status...");

  const requiresOnboarding = await checkIfRequiresOnboarding();

  if (requiresOnboarding) {
    console.warn("User needs to complete onboarding. Redirecting...");
    window.location.href = EPath.ONBOARDING;
    return;
  }

  window.location.href = EPath.UNAUTHORIZED;
};

const isAuthError = (response: Response): boolean => {
  return response?.status === HttpStatusCode.Unauthorized;
};

const isPermissionError = (response: Response): boolean => {
  return response?.status === HttpStatusCode.Forbidden;
};

/**
 * Middleware to handle token expiration and redirect to login
 * This middleware intercepts API requests and responses:
 * - Pre: Injects the latest token from storage into each request
 * - Post: Handles 401/403 errors with automatic token refresh and request retry
 * When token expires, it attempts to refresh and automatically retries the request
 */
export const apiMiddleWare: Middleware = {
  pre: async (context) => {
    const { init } = context;

    // Get the latest token from storage
    const token = secureStorage.get(SecureStorageKeys.CUSTOM_TOKEN);

    if (token) {
      // Update the Authorization header with the latest token
      const headers = new Headers(init.headers);
      headers.set("Authorization", `Bearer ${token}`);

      return {
        ...context,
        init: {
          ...init,
          headers,
        },
      };
    }

    return context;
  },
  post: async (context) => {
    const { response, url, init } = context;

    if (isAuthError(response)) {
      // Check if this is already a retry attempt to prevent infinite loops
      const isRetry =
        init.headers instanceof Headers &&
        init.headers.get("X-Retry-After-Refresh") === "true";

      if (isRetry) {
        // Already retried once, don't retry again - redirect to login
        console.warn("Retry after token refresh failed, redirecting to login");
        handleTokenExpiration();
        return;
      }

      // Attempt to refresh the token
      const newToken = await refreshToken();

      if (newToken) {
        // Token refreshed successfully - retry the original request
        console.log("Token refreshed, retrying request automatically");

        // Create new headers with the refreshed token and retry flag
        const retryHeaders = new Headers(init.headers);
        retryHeaders.set("Authorization", `Bearer ${newToken}`);
        retryHeaders.set("X-Retry-After-Refresh", "true");

        // Retry the request with the new token
        const retryResponse = await fetch(url, {
          ...init,
          headers: retryHeaders,
        });

        // Return the retry response (will be processed by subsequent middleware if any)
        return retryResponse;
      } else {
        // Token refresh failed - redirect to login
        handleTokenExpiration();
      }
      return;
    }

    if (isPermissionError(response)) {
      await handlePermissionError();
      return;
    }

    if (response?.ok === false) {
      const errorMessage = await extractErrorMessage(response);
      console.error("Error Message:", errorMessage);
      throw new ResponseError(response, errorMessage);
    }

    return response;
  },
};
